import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { db } from "../../lib/firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { Spinner } from "../../components/common/Spinner";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";

type DeliveryAgentSectionProps = {
  trigger?: number;
  onAdminEvent?: () => void;
};

export const DeliveryAgentSection: React.FC<DeliveryAgentSectionProps> = ({
  trigger,
  onAdminEvent,
}) => {
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"pending" | "approved">("pending");

  useEffect(() => {
    async function fetchAgents() {
      setLoading(true);
      try {
        const agentsRef = collection(db, "users");
        const q = query(
          agentsRef,
          where("roles", "array-contains", "deliveryAgent"),
          where("approved", "==", filter === "approved")
        );
        const snapshot = await getDocs(q);
        setAgents(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
      } catch (error) {
        console.error("Error fetching agents:", error);
        toast.error("Failed to fetch delivery agents.");
      } finally {
        setLoading(false);
      }
    }
    fetchAgents();
  }, [trigger, filter]);

  const { mutate: approveAgent, isLoading: isApproving } = useMutation(
    async (agentId: string) => {
      const agentRef = doc(db, "users", agentId);
      await updateDoc(agentRef, { approved: true });
    },
    {
      onSuccess: () => {
        toast.success("Agent approved!");
        onAdminEvent && onAdminEvent();
        setFilter("approved");
      },
      onError: (error: any) =>
        toast.error(error?.message || "Failed to approve agent."),
    }
  );

  return (
    <Card>
      <h2 className="text-xl font-bold mb-4">Manage Delivery Agents</h2>

      <div className="flex gap-2 mb-4">
        <Button
          variant={filter === "approved" ? "primary" : "secondary"}
          onClick={() => setFilter("approved")}
        >
          Approved Agents ({filter === "approved" ? agents.length : "..."})
        </Button>
        <Button
          variant={filter === "pending" ? "primary" : "secondary"}
          onClick={() => setFilter("pending")}
        >
          Pending Approval ({filter === "pending" ? agents.length : "..."})
        </Button>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-left text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2">Name</th>
                <th className="p-2">Phone</th>
                <th className="p-2">Email</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent: any) => (
                <tr key={agent.id} className="border-b">
                  <td className="p-2 font-medium">
                    {agent.displayName || agent.name}
                  </td>
                  <td className="p-2">{agent.phone}</td>
                  <td className="p-2">{agent.email}</td>
                  <td className="p-2">
                    {filter === "pending" ? (
                      <Button
                        variant="primary"
                        onClick={() => approveAgent(agent.id)}
                        isLoading={isApproving}
                      >
                        Approve
                      </Button>
                    ) : (
                      <span className="text-green-600 font-semibold">
                        Approved
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {agents.length === 0 && !loading && (
        <p className="text-gray-500 mt-4">No {filter} agents found.</p>
      )}
    </Card>
  );
};
